import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '@/lib/api-client';
import { formatCurrency, type Currency } from '@/utils/currency';
import type { PublicOrganization, PublicService } from './types';

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  const body = await response.json();
  return body.data as T;
}

export function usePublicBookingPage(slug: string | undefined) {
  const organization = useQuery({
    queryKey: ['public-booking-page', slug],
    queryFn: () => getJson<PublicOrganization>(`/v1/organizations/${slug}`),
    enabled: !!slug,
    retry: false,
  });

  const orgId = organization.data?.id;

  const services = useQuery({
    queryKey: ['public-booking-page', slug, 'services'],
    queryFn: () => getJson<PublicService[]>(`/v1/services?organization_id=${orgId}`),
    enabled: !!orgId,
  });

  return {
    organization: organization.data,
    // The API's formatted_price ignores the org currency; format it here.
    services: (services.data ?? [])
      .filter((s) => s.active)
      .map((s) => ({ ...s, formatted_price: formatCurrency(s.price, (organization.data?.currency || 'EUR') as Currency) })),
    isLoading: organization.isLoading || (!!orgId && services.isLoading),
    isError: organization.isError,
  };
}
